import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout'
import Head from 'next/head'
import Error from 'next/error'
import NextLink from 'next/link'
import { useRouter } from 'next/router';
import Container from '../../components/Container';
import CollapseComponent from '../../components/CollapseComponent';
import ExternalLink from '../../components/ExternalLink';
import { Box, LinkBox, Button,Center, Spinner, Heading, Flex, Text, Link, LinkOverlay, Badge, Divider, Wrap, WrapItem, Stack, HStack, VStack, useColorModeValue, Spacer, SimpleGrid, Container as ChakraContainer } from "@chakra-ui/react";
import prisma from '../../lib/prisma'
import { useUser } from "@clerk/nextjs";
import useSWR from "swr";

const fetcher = (url) =>
  fetch(url, {
    method: 'GET',
    headers: new Headers({ 'Content-Type': 'application/json'}),
    // credentials: 'same-origin',
  }).then((res) => res.json())

export default function Gene({errorCode, results}) {
    const gene = results.gene
    const router = useRouter();
    const { user } = useUser()
    // console.log(gene);
    // console.log(gene.phenotypes.map(pheno => console.log(pheno.phenotype.ghrConditions)));
    if (!gene) {
        return <h1>Sorry, gene not found!</h1>
    }

    if (errorCode) {
        return <Error statusCode={errorCode} />
    }
    const borderColor = useColorModeValue("gray.200", "neutral.900")
    function formatNumber(num) {
        return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
      }

    const cardBg = useColorModeValue("gray.100", "neutral.1000")

    function useUserGeneVariants() {
        const { data, error, isValidating } = useSWR(`/api/user/getGeneVariantsCount?gene=${gene.symbol}`,fetcher, {dedupingInterval: 15000, revalidateOnFocus: false })
        // console.log(error);
        return {
          data: data,
          isLoading: !error && !data,
          isError: error
        }
      }

      const { data: userGeneVariantsCount, isLoading: userGeneVariantsCountLoading, isError: userGeneVariantsCountError } = useUserGeneVariants()

    return (
        <Layout>
            <Head>
                <title>{gene.symbol} Gene</title>
                <meta name="robots" content="index, follow"></meta>
            </Head>
            <Container>
                <ChakraContainer maxW={'6xl'} mb={100}>
                <Flex columns={3} mt={70}>
                    <Box>
                    <Stack w={['80%', 'full']}>
                    <Heading as="h1" fontSize="4xl" mt={70}>{gene.symbol} Gene</Heading>
                    <Heading as="h2" fontSize="xl" mt={2}>{gene.name}</Heading>
                    {gene.alias_symbols.length > 0 ? 
                    <Box mt={7}>
                        <Text fontSize="sm" fontWeight="bold" as="h3">ALIAS SYMBOLS</Text>
                        <Wrap>
                        {gene.alias_symbols.map(alias => <WrapItem key={alias}><Text fontSize="sm" fontWeight="normal" borderRadius={'lg'}>{alias}</Text></WrapItem>)}
                        </Wrap>
                    </Box>
                    : null}
                    </Stack>
                    </Box>
                    <Spacer />
            {user ? 
            <Box border={'1px'} borderRadius={10} p={5} borderColor={borderColor}>
                <Center>
                <VStack>
                <Heading size="md">Your Results</Heading>
                {userGeneVariantsCountLoading ? <Spinner color="cyan.500" size="lg" thickness="3px"/> :
                <Box>
                {userGeneVariantsCount.count > 0 ? 
                <Box>
                    {/* <Center my="3">
                {userPhenotype.results[0].significance.length > 0 ?
                userPhenotype.results[0].significance.map((significance, index) => <Badge key={index} px={3} py={1} borderRadius={30} fontSize={"xs"} colorScheme={significance == 'risk' ? 'red' : significance == 'carrier' ? 'orange' : 'gray'}>{significance}</Badge>)
                :     <Badge px={3} py={1} borderRadius={30} fontSize={"xs"}>Undetermined</Badge>
            }
                </Center>
                <Divider my={5}/> */}
                <Center>
                <NextLink href={`${router.asPath}/my-variants`} passHref>
                <Link fontSize={'sm'} fontWeight={'bold'}>{userGeneVariantsCount.count} {userGeneVariantsCount.count > 1 ? 'VARIANTS' : 'VARIANT'} →</Link>
                </NextLink>
                </Center>
                </Box>
                :
                <Text fontSize="13">You are not associated with this gene</Text>
                }
                </Box>
                }
                </VStack>
                </Center>
                
            </Box>
            : 
            <Box border={'1px'} borderRadius={10} p={5} borderColor={'gray.200'}>
                
            <Center>
            <VStack>
            <Heading size="md">Your Results</Heading>
            <Button
                as={'a'}
                fontSize={'sm'}
                fontWeight={600}
                variant={'link'}
                href={'/signin'}>
                Sign In
                </Button>
            
            </VStack>
            </Center>
            
        </Box>
        }
        </Flex>
                    <Divider mb={10} mt={10} />
                    <Flex
                        direction={['column-reverse', 'column-reverse', 'row', 'row']}
                        w="full"
                        justify="space-between"
                    >
                    <Box mt={[8, 8, 0]} mb={3} minW={['', 'xl']} maxW={['2xl', '2xl', '2lg', '3xl']}>
                        <Heading as="h3" mb={3} fontSize="lg" fontWeight="bold">Description</Heading>
                        <Box width={['100%','100%', '90%']} dangerouslySetInnerHTML={{ __html: `${gene.ghr_genes.length > 0 ? gene.ghr_genes[0].description : "No description available."}` }}></Box>
                    </Box>
                    <Flex flexDirection={["row", "row", "column", "column"]}>
                    <Box border="1px" p={5} borderRadius={10} borderColor={borderColor} w={['50%', '100%']}>
                        <Heading as="h3" fontSize="10" color="gray.500">CHROMOSOME</Heading>
                        <Text fontSize="md">{gene.chr}</Text>
                        <Divider my={3}/>
                        <Heading as="h3" fontSize="10" color="gray.500">LOCATION</Heading>
                        <Text fontSize="md">{gene.chr_loc}</Text>
                        <Divider my={3}/>
                        <Heading as="h3" fontSize="10" color="gray.500">LOCUS TYPE</Heading>
                        <Text fontSize="md">{gene.locus_type}</Text>
                    </Box>
                    <Flex flexDirection={["column", "column"]} mt={[0,0,4]} ml={['10%','25%',0]} bg={cardBg} w={['50%', '100%']} borderRadius={10} p={5} textAlign={["right", "right","right"]}>
                        <Box mb={[0,0,16]}>
                        <Text fontSize="12" color="gray.500" fontWeight="bold">VARIANTS</Text>
                        <Text fontSize="3xl" fontWeight="normal">{formatNumber(gene._count.variants)}</Text>
                        </Box>
                        <Box  marginTop="auto" textAlign="right">
                        {gene._count.variants > 0 ? 
                        <Text fontSize="sm" fontWeight="bold"  bottom='5' right='5'>
                        <Link as={NextLink} href={`/genes/${gene.slug}/variants`}>SEE THE VARIANTS →</Link>
                        </Text>

                        : null}
                        </Box>
                    </Flex>
                    </Flex>
                    </Flex>
                    <Flex
                    direction={['column-reverse', 'column-reverse', 'row', 'row']}
                    w="full"
                    justify="space-between"

                    >
                    <Box mt={[8, 8, 30]} mb={3} minW={['', 'xl']} maxW={'2xl'}>
                        <Heading as="h3" mb={3} fontSize="lg" fontWeight="bold">Phenotypes</Heading>
                        <Stack spacing={5}>
                        {gene.phenotypes.map(phenotype => 
                        <LinkBox border='1px' borderRadius={10} borderColor={borderColor} minH={100} p={6} key={phenotype.phenotypes.slug} >
                        <Text fontSize={'lg'} fontWeight='bold'>
                        <NextLink key={phenotype.phenotypes.name} href={`/phenotypes/${phenotype.phenotypes.slug}`} passHref>
                        <LinkOverlay>{phenotype.phenotypes.name}</LinkOverlay>
                        </NextLink>
                        </Text>
                        {phenotype.phenotypes.ghr_conditions && phenotype.phenotypes.ghr_conditions.length > 0 ? <CollapseComponent collapseText={phenotype.phenotypes.ghr_conditions[0].ghr_conditions.description}/> : <CollapseComponent collapseText={"No description available."}/>}
                        </LinkBox>
                        )}
                        </Stack>
                    </Box>
                    </Flex>
                    <Box mt={10}>
                    <Heading as="h3" mb={5} fontSize="lg" fontWeight="bold">External Links</Heading>
                    <Wrap maxW={['md','3xl']}>
                    <WrapItem>
                        <Box borderLeft="1px" pl={3} borderColor={borderColor}>
                            <Text as="h5" fontWeight="normal" fontSize={'sm'} color="gray.500">HGNC</Text>
                            <ExternalLink 
                                fontSize={'sm'} 
                                link={`https://www.genenames.org/data/gene-symbol-report/#!/hgnc_id/${gene.hgnc_id}`} 
                                linktext={gene.hgnc_id} 
                            />
                        </Box>
                    </WrapItem>
                    <WrapItem>
                    <Box borderLeft="1px" pl={3} borderColor={borderColor}>
                    <Text as="h5" fontWeight="normal" fontSize={'sm'} color="gray.500">Ensembl</Text>
                    <ExternalLink 
                        fontSize={'sm'} 
                        link={`https://www.ensembl.org/Homo_sapiens/Gene/Summary?g=${gene.ensembl_id}`} 
                        linktext={gene.ensembl_id} 
                    />
                    </Box>
                    </WrapItem>
                    <WrapItem>
                    <Box borderLeft="1px" pl={3} borderColor={borderColor}>
                    <Text as="h5" fontWeight="normal" fontSize={'sm'} color="gray.500">NCBI</Text>
                    <ExternalLink 
                        fontSize={'sm'} 
                        link={`https://www.ncbi.nlm.nih.gov/gene/${gene.ncbi_id}`} 
                        linktext={gene.ncbi_id} 
                    />
                    </Box>
                    </WrapItem>
                    <WrapItem>
                    <Box borderLeft="1px" pl={3} borderColor={borderColor}>
                    <Text as="h5" fontWeight="normal" fontSize={'sm'} color="gray.500">OMIM</Text>
                    <ExternalLink 
                        fontSize={'sm'} 
                        link={`https://www.omim.org/entry/${gene.mim_id}`} 
                        linktext={gene.mim_id} 
                    />
                    </Box>
                    </WrapItem>
                    </Wrap>
                    </Box>
                </ChakraContainer>
                </Container>
        </Layout>
    )
}

export async function getServerSideProps(context) {
    // console.log(context);
    var queryArgs ={}
    queryArgs = {
        slug: context.params.slug
    }
    // let query = supabase
    // .from('genes')
    // .select('*, ghr: ghr_genes(description), variants: variant_genes(count), phenotypes(name, slug, ghr_conditions(description))')

    const [genes, totalQuery, totalVariants] = await prisma.$transaction([
        prisma.genes.findUnique({
          where: queryArgs,
          include: {
              ghr_genes: true,
            phenotypes: {
              select: {
                phenotypes: {
                  select: {
                    name: true,
                    slug: true,
                    ghr_conditions: {
                        select: {
                            ghr_conditions: {
                                select: {
                                    description: true
                                }
                            }
                        }
                    }
                  }
                }
              }
            },
            _count: {
                select: { variants: true },
              },
          }
          }),
        // prisma.gene.count({where: queryArgs}),
        // prisma.gene.count({where: {
        //   phenotypes: {
        //     some: {
        //       phenotype: {
        //         slug: context.params.slug
        //       }
        //     }
        //   }
        // }})
      ])

      if (!genes) {
        return {
            notFound: true
        }
}

    // query = query.eq('slug', context.params.slug)
    const data = JSON.stringify(genes, (key, value) => (typeof value === 'bigint' ? value.toString() : value))
    let results  = {gene: JSON.parse(data)}
    // console.log(results);

    return { props: { results } }
    }

// export async function getServerSideProps(context) {
//     const res = await fetch(`http://127.0.0.1:8000/api/genes/${context.params.slug}`)
//     const gene = await res.json();
//     const errorCode = res.status == 200 ? false : res.status;

//     if (errorCode) {
//         res.statusCode = errorCode;
//       }

//     return {
//         props: {
//             errorCode,
//             gene,
//         }
//     };
// }